/**
 * Supabase calls for programs, sequences, suppressions, custom fields and the
 * outreach dashboard. Islands import from here and never call tables directly.
 * RLS decides what each role may write; errors surface as plain Error messages.
 */

import { supabase } from '../supabase'
import { getSetting } from '../settings'
import { fetchMyWorkspaces } from '../workspace'
import { parseRamp } from './ramp'
import { normalizeOverview, normalizeStats } from './stats'
import { customFieldContains, parseSavedViews, SPECIAL_STAGES } from './savedViews'
import type { SavedView, ViewFilters } from './savedViews'
import type {
  CustomFieldDef,
  CustomFieldEntity,
  CustomFieldType,
  CustomFieldValues,
  DayKey,
  EnrollResult,
  OutreachOverview,
  Program,
  ProgramInput,
  ProgramStatus,
  Sequence,
  SequenceStep,
  SiteRef,
  Suppression,
  SuppressionInput,
  WorkspaceStats,
} from './types'

type Row = Record<string, unknown>

function fail(error: { message: string } | null): void {
  if (error) throw new Error(error.message)
}

function num(v: unknown, fallback = 0): number {
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : fallback
}

// ── workspace helpers ────────────────────────────────────────────────────

let cachedWorkspaceId: string | null = null

/** Id of the business this tab works in, as the server sees it. */
export async function currentWorkspaceId(): Promise<string> {
  if (cachedWorkspaceId) return cachedWorkspaceId
  const rows = await fetchMyWorkspaces()
  const cur = rows.find((r) => r.is_current) ?? rows[0]
  if (!cur) throw new Error('No business found for this login.')
  cachedWorkspaceId = cur.id
  return cur.id
}

/** The caller's role in the current business. */
export async function currentRole(): Promise<'owner' | 'admin' | 'member'> {
  const rows = await fetchMyWorkspaces()
  const cur = rows.find((r) => r.is_current) ?? rows[0]
  return cur?.role ?? 'member'
}

export function _resetOutreachApiForTests(): void {
  cachedWorkspaceId = null
}

// ── sites ────────────────────────────────────────────────────────────────

export async function listSites(): Promise<SiteRef[]> {
  const { data, error } = await supabase.from('sites').select('id,name,domain').order('name')
  fail(error)
  return ((data ?? []) as Row[]).map((r) => ({
    id: String(r.id),
    name: String(r.name),
    domain: String(r.domain),
  }))
}

// ── programs ─────────────────────────────────────────────────────────────

function toProgram(r: Row): Program {
  return {
    id: String(r.id),
    workspace_id: String(r.workspace_id),
    site_id: (r.site_id as string | null) ?? null,
    name: String(r.name),
    kind: (r.kind as Program['kind']) ?? 'other',
    from_label: (r.from_label as string | null) ?? null,
    daily_cap: num(r.daily_cap),
    ramp: parseRamp(r.ramp),
    cooldown_days: num(r.cooldown_days, 30),
    never_same_address: r.never_same_address !== false,
    bounce_stop_pct: num(r.bounce_stop_pct, 2),
    bounce_min_sample: num(r.bounce_min_sample, 20),
    send_days: Array.isArray(r.send_days) ? (r.send_days as DayKey[]) : [],
    blocked_phrases: Array.isArray(r.blocked_phrases) ? (r.blocked_phrases as string[]) : [],
    active: r.active === true,
    sequence_id: (r.sequence_id as string | null) ?? null,
    created_at: String(r.created_at ?? ''),
    updated_at: String(r.updated_at ?? ''),
  }
}

export async function listPrograms(): Promise<Program[]> {
  const { data, error } = await supabase.from('programs').select('*').order('created_at')
  fail(error)
  return ((data ?? []) as Row[]).map(toProgram)
}

export async function saveProgram(id: string | null, input: ProgramInput): Promise<Program> {
  const payload = { ...input, ramp: input.ramp }
  const q = id
    ? supabase.from('programs').update(payload).eq('id', id)
    : supabase.from('programs').insert(payload)
  const { data, error } = await q.select('*').single()
  fail(error)
  return toProgram(data as Row)
}

export async function deleteProgram(id: string): Promise<void> {
  const { error } = await supabase.from('programs').delete().eq('id', id)
  fail(error)
}

/** The kill switch. */
export async function setProgramActive(id: string, active: boolean): Promise<void> {
  const { error } = await supabase.from('programs').update({ active }).eq('id', id)
  fail(error)
}

export async function fetchProgramStatus(programId: string): Promise<ProgramStatus | null> {
  const { data, error } = await supabase.rpc('program_status', { p_program: programId })
  fail(error)
  const r = (Array.isArray(data) ? data[0] : data) as Row | undefined
  if (!r) return null
  return {
    program_id: String(r.program_id),
    workspace_id: String(r.workspace_id),
    timezone: String(r.timezone ?? 'UTC'),
    local_date: String(r.local_date ?? ''),
    local_day: String(r.local_day ?? 'mon') as DayKey,
    active: r.active === true,
    cap_today: num(r.cap_today),
    sent_today: num(r.sent_today),
    claims_pending: num(r.claims_pending),
    remaining: num(r.remaining),
    sent_7d: num(r.sent_7d),
    bounces_7d: num(r.bounces_7d),
    bounce_rate_7d: num(r.bounce_rate_7d),
    paused_reason: (r.paused_reason as ProgramStatus['paused_reason']) ?? null,
  }
}

// ── sequences ────────────────────────────────────────────────────────────

export async function listSequences(): Promise<Sequence[]> {
  const { data, error } = await supabase
    .from('sequences')
    .select('id,workspace_id,name,final_stage')
    .order('created_at')
  fail(error)
  return ((data ?? []) as Row[]).map((r) => ({
    id: String(r.id),
    workspace_id: String(r.workspace_id),
    name: String(r.name),
    final_stage: (r.final_stage as string | null) ?? null,
  }))
}

export async function listSteps(sequenceId: string): Promise<SequenceStep[]> {
  const { data, error } = await supabase
    .from('sequence_steps')
    .select('step_no,day_offset,subject,body,same_thread')
    .eq('sequence_id', sequenceId)
    .order('step_no')
  fail(error)
  return ((data ?? []) as Row[]).map((r) => ({
    step_no: num(r.step_no),
    day_offset: num(r.day_offset),
    subject: String(r.subject ?? ''),
    body: String(r.body ?? ''),
    same_thread: r.same_thread !== false,
  }))
}

/** Create or rename a sequence, then replace its steps in one transaction. */
export async function saveSequence(
  id: string | null,
  name: string,
  finalStage: string | null,
  steps: readonly Omit<SequenceStep, 'step_no'>[],
): Promise<string> {
  let seqId = id
  if (seqId) {
    const { error } = await supabase
      .from('sequences')
      .update({ name, final_stage: finalStage })
      .eq('id', seqId)
    fail(error)
  } else {
    const { data, error } = await supabase
      .from('sequences')
      .insert({ name, final_stage: finalStage })
      .select('id')
      .single()
    fail(error)
    seqId = String((data as Row).id)
  }
  const { error } = await supabase.rpc('save_sequence_steps', {
    p_sequence: seqId,
    p_steps: steps.map((s) => ({
      day_offset: s.day_offset,
      subject: s.subject,
      body: s.body,
      same_thread: s.same_thread,
    })),
  })
  fail(error)
  return seqId
}

export async function deleteSequence(id: string): Promise<void> {
  const { error } = await supabase.from('sequences').delete().eq('id', id)
  fail(error)
}

// ── suppressions ─────────────────────────────────────────────────────────

export const SUPPRESSION_PAGE = 100

export async function listSuppressions(
  opts: { search?: string; page?: number } = {},
): Promise<{ rows: Suppression[]; total: number }> {
  const page = Math.max(1, opts.page ?? 1)
  let q = supabase
    .from('suppressions')
    .select('id,kind,value,reason,source,created_at', { count: 'exact' })
    .order('created_at', { ascending: false })
    .range((page - 1) * SUPPRESSION_PAGE, page * SUPPRESSION_PAGE - 1)
  const term = (opts.search ?? '').trim().replace(/[%,()]/g, '')
  if (term) q = q.ilike('value', `%${term}%`)
  const { data, count, error } = await q
  fail(error)
  return { rows: (data ?? []) as Suppression[], total: count ?? 0 }
}

export async function addSuppressions(
  rows: readonly SuppressionInput[],
): Promise<{ added: number; skipped: number; invalid: number }> {
  let added = 0
  let skipped = 0
  let invalid = 0
  const CHUNK = 1000
  for (let i = 0; i < rows.length; i += CHUNK) {
    const { data, error } = await supabase.rpc('add_suppressions', {
      p_rows: rows.slice(i, i + CHUNK),
    })
    fail(error)
    const r = (data ?? {}) as Row
    added += num(r.added)
    skipped += num(r.skipped)
    invalid += num(r.invalid)
  }
  return { added, skipped, invalid }
}

export async function removeSuppression(id: string): Promise<void> {
  const { error } = await supabase.from('suppressions').delete().eq('id', id)
  fail(error)
}

// ── enrolment ────────────────────────────────────────────────────────────

export async function enrollContacts(
  programId: string,
  contactIds: readonly string[],
  manual = false,
): Promise<EnrollResult> {
  const total: EnrollResult = {
    requested: 0,
    enrolled: 0,
    already_enrolled: 0,
    no_email: 0,
    not_found: 0,
  }
  const CHUNK = 2000
  for (let i = 0; i < contactIds.length; i += CHUNK) {
    const { data, error } = await supabase.rpc('enroll_contacts', {
      p_program: programId,
      p_contact_ids: contactIds.slice(i, i + CHUNK),
      p_manual: manual,
    })
    fail(error)
    const r = (data ?? {}) as Row
    total.requested += num(r.requested)
    total.enrolled += num(r.enrolled)
    total.already_enrolled += num(r.already_enrolled)
    total.no_email += num(r.no_email)
    total.not_found += num(r.not_found)
  }
  return total
}

// ── saved views → contact ids ────────────────────────────────────────────

export function loadSavedViews(): SavedView[] {
  return parseSavedViews(getSetting('saved_views', ''))
}

/**
 * Contact ids matching a saved view or ad-hoc filter. Same semantics as the
 * contacts list (user_contacts inclusion first, then contacts), capped at
 * `max` ids so a bad filter cannot enrol the whole database by accident.
 */
export async function fetchContactIdsForFilters(
  f: ViewFilters,
  userId: string,
  defs: readonly CustomFieldDef[],
  max = 5000,
): Promise<{ ids: string[]; truncated: boolean }> {
  const stage = SPECIAL_STAGES.has(f.stage) ? '' : f.stage
  const contains = customFieldContains(f, defs)
  if (f.customField && !contains) return { ids: [], truncated: false }

  let includeIds: string[] | null = null
  if (stage || f.scoreMin || f.scoreMax || f.tagFilter || contains) {
    let uq = supabase.from('user_contacts').select('contact_id').eq('user_id', userId)
    if (stage) uq = uq.eq('lifecycle_stage', stage)
    if (f.scoreMin) uq = uq.gte('priority_score', Number(f.scoreMin))
    if (f.scoreMax) uq = uq.lte('priority_score', Number(f.scoreMax))
    if (f.tagFilter) uq = uq.contains('tags', [f.tagFilter])
    if (contains) uq = uq.contains('custom_fields', contains)
    const { data, error } = await uq.limit(max + 1)
    fail(error)
    includeIds = ((data ?? []) as Row[]).map((r) => String(r.contact_id))
    if (includeIds.length === 0) return { ids: [], truncated: false }
  }

  const ids: string[] = []
  const PAGE = 1000
  for (let from = 0; ids.length <= max; from += PAGE) {
    let q = supabase
      .from('contacts')
      .select('id')
      .not('primary_email', 'is', null)
      .order('id')
      .range(from, from + PAGE - 1)
    const search = f.search.trim().replace(/[%,()]/g, '')
    if (search) {
      q = q.or(
        `full_name.ilike.%${search}%,company_name.ilike.%${search}%,job_title.ilike.%${search}%,primary_email.ilike.%${search}%`,
      )
    }
    if (f.country) q = q.ilike('country', `%${f.country.replace(/[%,()]/g, '')}%`)
    if (f.location) {
      const loc = f.location.replace(/[%,()]/g, '')
      q = q.or(`city.ilike.%${loc}%,state.ilike.%${loc}%,country.ilike.%${loc}%`)
    }
    if (f.phonePrefix) q = q.ilike('primary_phone', `${f.phonePrefix.replace(/[%,()]/g, '')}%`)
    if (includeIds) q = q.in('id', includeIds.slice(0, max + 1))
    const { data, error } = await q
    fail(error)
    const batch = ((data ?? []) as Row[]).map((r) => String(r.id))
    ids.push(...batch)
    if (batch.length < PAGE) break
  }
  const truncated = ids.length > max
  return { ids: truncated ? ids.slice(0, max) : ids, truncated }
}

// ── custom fields ────────────────────────────────────────────────────────

function toDef(r: Row): CustomFieldDef {
  return {
    id: String(r.id),
    entity: r.entity as CustomFieldEntity,
    key: String(r.key),
    label: String(r.label),
    type: r.type as CustomFieldType,
    options: Array.isArray(r.options) ? (r.options as unknown[]).map(String) : [],
    position: num(r.position),
  }
}

export async function listCustomFieldDefs(entity?: CustomFieldEntity): Promise<CustomFieldDef[]> {
  let q = supabase
    .from('custom_field_defs')
    .select('id,entity,key,label,type,options,position')
    .order('position')
    .order('created_at')
  if (entity) q = q.eq('entity', entity)
  const { data, error } = await q
  fail(error)
  return ((data ?? []) as Row[]).map(toDef)
}

export async function saveCustomFieldDef(
  id: string | null,
  def: Pick<CustomFieldDef, 'entity' | 'key' | 'label' | 'type' | 'options' | 'position'>,
): Promise<CustomFieldDef> {
  // The key and entity never change after creation: values are stored under the key.
  const q = id
    ? supabase
        .from('custom_field_defs')
        .update({
          label: def.label,
          type: def.type,
          options: def.options,
          position: def.position,
        })
        .eq('id', id)
    : supabase.from('custom_field_defs').insert(def)
  const { data, error } = await q.select('id,entity,key,label,type,options,position').single()
  fail(error)
  return toDef(data as Row)
}

export async function deleteCustomFieldDef(id: string): Promise<void> {
  const { error } = await supabase.from('custom_field_defs').delete().eq('id', id)
  fail(error)
}

export async function fetchContactCustomFields(
  contactId: string,
  userId: string,
): Promise<CustomFieldValues> {
  const { data, error } = await supabase
    .from('user_contacts')
    .select('custom_fields')
    .eq('user_id', userId)
    .eq('contact_id', contactId)
    .maybeSingle()
  fail(error)
  return ((data as Row | null)?.custom_fields as CustomFieldValues | null) ?? {}
}

export async function saveContactCustomFields(
  contactId: string,
  patch: CustomFieldValues,
): Promise<void> {
  const { error } = await supabase.rpc('merge_contact_custom_fields', {
    p_rows: [{ contact_id: contactId, fields: patch }],
  })
  fail(error)
}

/** Bulk merge used by CSV import: one call per chunk. */
export async function saveContactCustomFieldsBulk(
  rows: ReadonlyArray<{ contact_id: string; fields: CustomFieldValues }>,
): Promise<void> {
  const CHUNK = 500
  for (let i = 0; i < rows.length; i += CHUNK) {
    const { error } = await supabase.rpc('merge_contact_custom_fields', {
      p_rows: rows.slice(i, i + CHUNK),
    })
    fail(error)
  }
}

export async function fetchAccountCustomFields(accountId: string): Promise<CustomFieldValues> {
  const { data, error } = await supabase
    .from('accounts')
    .select('custom_fields')
    .eq('id', accountId)
    .maybeSingle()
  fail(error)
  return ((data as Row | null)?.custom_fields as CustomFieldValues | null) ?? {}
}

export async function saveAccountCustomFields(
  accountId: string,
  patch: CustomFieldValues,
): Promise<void> {
  const { error } = await supabase.rpc('merge_account_custom_fields', {
    p_account: accountId,
    p_patch: patch,
  })
  fail(error)
}

// ── business-level settings (timezone, blocked phrases) ──────────────────

export async function fetchOutreachSettings(): Promise<{
  timezone: string
  blockedPhrases: string[]
}> {
  const { data, error } = await supabase
    .from('workspace_settings')
    .select('key,value')
    .in('key', ['timezone', 'blocked_phrases'])
  fail(error)
  let timezone = 'UTC'
  let blockedPhrases: string[] = []
  for (const r of (data ?? []) as Row[]) {
    if (r.key === 'timezone' && typeof r.value === 'string' && r.value.trim()) timezone = r.value
    if (r.key === 'blocked_phrases' && Array.isArray(r.value)) {
      blockedPhrases = (r.value as unknown[]).map(String)
    }
  }
  return { timezone, blockedPhrases }
}

export async function saveWorkspaceSetting(
  key: 'timezone' | 'blocked_phrases',
  value: unknown,
): Promise<void> {
  const workspaceId = await currentWorkspaceId()
  const { error } = await supabase
    .from('workspace_settings')
    .upsert(
      { workspace_id: workspaceId, key, value, updated_at: new Date().toISOString() },
      { onConflict: 'workspace_id,key' },
    )
  fail(error)
}

// ── dashboard ────────────────────────────────────────────────────────────

export async function fetchStats(
  opts: { workspaceId?: string; from?: Date; to?: Date } = {},
): Promise<WorkspaceStats> {
  const { data, error } = await supabase.rpc('program_stats', {
    p_workspace: opts.workspaceId ?? null,
    p_from: opts.from ? opts.from.toISOString() : null,
    p_to: opts.to ? opts.to.toISOString() : null,
  })
  fail(error)
  return normalizeStats(data)
}

export async function fetchOutreachOverview(
  opts: { from?: Date; to?: Date } = {},
): Promise<OutreachOverview> {
  const { data, error } = await supabase.rpc('outreach_overview', {
    p_from: opts.from ? opts.from.toISOString() : null,
    p_to: opts.to ? opts.to.toISOString() : null,
  })
  fail(error)
  return normalizeOverview(data)
}
